import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { docsApi } from '../api';
import { useAuth } from '../context/AuthContext';
import DocumentCard from '../components/documents/DocumentCard';
import DocumentModal from '../components/documents/DocumentModal';
import DocumentDetailModal from '../components/documents/DocumentDetailModal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { toast } from 'react-toastify';

/** Returns time-appropriate greeting */
function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

export default function DashboardPage() {
  const { user }  = useAuth();
  const location  = useLocation();
  const navigate  = useNavigate();

  const [docs,      setDocs]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [stats,     setStats]     = useState({ total: 0, totalHits: 0 });

  const [showCreate, setShowCreate] = useState(false);
  const [editDoc,    setEditDoc]    = useState(null);
  const [viewDoc,    setViewDoc]    = useState(null);
  const [deleteDoc,  setDeleteDoc]  = useState(null);

  const [viewedDocs, setViewedDocs] = useState(new Set());

  // Open create modal when ?new=1 is in URL (triggered from topbar)
  useEffect(() => {
    if (new URLSearchParams(location.search).get('new') === '1') {
      setShowCreate(true);
      navigate('/', { replace: true });
    }
  }, [location.search, navigate]);

  const loadDocs = useCallback(async () => {
    setLoading(true);

    // 2. Reset the tracking Set on load
    setViewedDocs(new Set());
    try {
      const { data } = await docsApi.search({ query: ' ', page: 0, size: 100 });
      setDocs(data);
      setStats({
        total:     data.length,
        totalHits: data.reduce((s, d) => s + d.hitCount, 0),
      });
    } catch {
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  const handleDelete = async () => {
    try {
      await docsApi.delete(deleteDoc.id);
      toast.success('Document deleted');
      setDeleteDoc(null);
      loadDocs();
    } catch {
      toast.error('Failed to delete document');
    }
  };

  const mostAccessed = [...docs].sort((a, b) => b.hitCount - a.hitCount)[0];

  // Greet by firstName if available, fall back to username
  const firstName = user?.firstName || user?.username || 'there';

  return (
    <>
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">{greeting()}, {firstName} 👋</h1>
          <p className="page-subtitle">Here's an overview of your document library</p>
        </div>
        <button className="btn-primary-custom" onClick={() => setShowCreate(true)}>
          + New Document
        </button>
      </div>

      {/* Stats */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3">
          <div className="stat-card">
            <div className="stat-icon blue">📄</div>
            <div>
              <div className="stat-value">{stats.total}</div>
              <div className="stat-label">Total Documents</div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="stat-card">
            <div className="stat-icon green">👁</div>
            <div>
              <div className="stat-value">{stats.totalHits}</div>
              <div className="stat-label">Total Views</div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="stat-card">
            <div className="stat-icon amber">🏷</div>
            <div>
              <div className="stat-value">
                {[...new Set(docs.flatMap(d => d.tags ?? []))].length}
              </div>
              <div className="stat-label">Unique Tags</div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="stat-card">
            <div className="stat-icon red">🔥</div>
            <div>
              <div className="stat-value">{mostAccessed?.hitCount ?? 0}</div>
              <div className="stat-label">Top Doc Hits</div>
            </div>
          </div>
        </div>
      </div>

      {/* Document Grid */}
      <div className="page-header" style={{ marginBottom: '1rem' }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 600, margin: 0 }}>All Documents</h2>
        <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
          {docs.length} document{docs.length !== 1 ? 's' : ''}
        </span>
      </div>

      {loading ? (
        <div className="loading-overlay"><span className="spinner" /></div>
      ) : docs.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📭</div>
          <div className="empty-state-title">No documents yet</div>
          <div className="empty-state-text">Create your first document to get started</div>
          <button className="btn-primary-custom" onClick={() => setShowCreate(true)}>
            + New Document
          </button>
        </div>
      ) : (
        <div className="row g-3">
          {docs.map(doc => (
            <div key={doc.id} className="col-12 col-sm-6 col-lg-4">
              <DocumentCard
                doc={doc}
                onClick={async () => {
                  if (!viewedDocs.has(doc.id)) {
                    try {
                      const { data } = await docsApi.getById(doc.id);
                      setViewDoc(data);
                      setViewedDocs(prev => new Set(prev).add(doc.id));
                    } catch (error) {
                      setViewDoc(doc);
                    }
                  } else {
                    setViewDoc(doc);
                  }
                }}
                onEdit={d => setEditDoc(d)}
                onDelete={d => setDeleteDoc(d)}
              />
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {showCreate && (
        <DocumentModal onClose={() => setShowCreate(false)} onSaved={loadDocs} />
      )}
      {editDoc && (
        <DocumentModal doc={editDoc} onClose={() => setEditDoc(null)} onSaved={loadDocs} />
      )}
      {viewDoc && (
        <DocumentDetailModal
          doc={viewDoc}
          onClose={() => setViewDoc(null)}
          onEdit={d => { setViewDoc(null); setEditDoc(d); }}
        />
      )}
      {deleteDoc && (
        <ConfirmDialog
          title="Delete Document"
          message={`"${deleteDoc.title}" will be permanently deleted.`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteDoc(null)}
        />
      )}
    </>
  );
}
